import { useEffect, useRef, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { Button } from './Button';

export interface SheetProps {
  open: boolean;
  onClose: () => void;
  /** Titre lu par les lecteurs d'écran ; affiché si `showTitle` est vrai */
  title: string;
  showTitle?: boolean;
  /** Si vrai, présente le sheet centré (modale) ; sinon, slide-up depuis le bas */
  centered?: boolean;
  /** Largeur max au-delà de mobile */
  maxWidthClass?: string;
  children: ReactNode;
}

/**
 * Sheet : bottom-sheet mobile (slide-up) ou modale centrée.
 * - aria-modal, role="dialog", title via aria-labelledby
 * - escape key ferme
 * - click backdrop ferme
 * - focus trappé sur première action focusable (basique, sans bibliothèque)
 */
export const Sheet = ({ open, onClose, title, showTitle = true, centered = false, maxWidthClass = 'max-w-md', children }: SheetProps) => {
  const titleId = `sheet-title-${title.replace(/\s+/g, '-').toLowerCase()}`;
  const panelRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    previousFocusRef.current = document.activeElement as HTMLElement | null;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    // Focus le premier bouton/input du panel
    const t = setTimeout(() => {
      const focusable = panelRef.current?.querySelector<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      focusable?.focus();
    }, 50);
    return () => {
      clearTimeout(t);
      document.removeEventListener('keydown', onKey);
      previousFocusRef.current?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;

  const containerCls = centered
    ? 'fixed inset-0 z-50 flex items-center justify-center p-4'
    : 'fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4';

  return (
    <div
      className={containerCls}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <div
        className="absolute inset-0 bg-zinc-950/80 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        className={[
          'relative bg-zinc-900 border border-zinc-800 rounded-2xl w-full',
          maxWidthClass,
          'max-h-[85vh] overflow-y-auto pb-safe',
          centered ? 'animate-slide-up' : 'animate-slide-up',
        ].join(' ')}
      >
        <div className="flex justify-between items-center p-4 sticky top-0 bg-zinc-900/95 backdrop-blur-sm border-b border-zinc-800/50 z-10">
          <h3 id={titleId} className={`text-lg font-medium ${showTitle ? '' : 'sr-only'}`}>
            {title}
          </h3>
          <Button variant="ghost" iconOnly aria-label="Fermer" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
};
